import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { authService } from '../services/authService';
import { setCredentials } from '../store/authSlice';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { 
  ShieldCheckIcon, 
  EnvelopeIcon, 
  ArrowPathIcon, 
  ExclamationCircleIcon, 
  CheckCircleIcon, 
  LockClosedIcon 
} from '@heroicons/react/24/outline';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { darkMode } = useTheme();
  
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Get email from location state if available
    if (location.state?.email) {
      setEmail(location.state.email);
    } else {
      // Try to get from localStorage
      const pendingRegistration = localStorage.getItem('pendingRegistration');
      if (pendingRegistration) {
        const data = JSON.parse(pendingRegistration);
        setEmail(data.email);
      }
    }
    
    if (location.state?.isNewRegistration) {
      setMessage('A verification code has been sent to your email. Please check your inbox and enter the code below.');
    }
  }, [location]);

  // Handle countdown for resend button
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim() || !email.trim()) {
      setError('Please enter your email and verification code');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await authService.verifyEmail(email, code);
      
      setIsVerified(true);
      dispatch(setCredentials(response));
      setMessage('Email verified successfully!');
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    if (!email) {
      setError('Email address is missing.');
      return;
    }
    try {
      setLoading(true);
      setMessage('');
      setError('');
      setResendDisabled(true);
      setCountdown(60); // 60 second cooldown
      
      // Use the new public resend function
      const response = await authService.resendVerificationEmailPublic(email); 
      setMessage(response.message || 'A new verification code has been sent to your email.');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to resend code');
      setResendDisabled(false);
      setCountdown(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError('');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setCode(value);
    setError('');
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`max-w-md w-full space-y-8 p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl`}
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-purple-600 shadow-md shadow-indigo-500/30"
          >
            <ShieldCheckIcon className="h-8 w-8 text-white" />
          </motion.div>
          
          <h2 className="mt-6 text-center text-3xl font-extrabold">
            Verify Your Email
          </h2>
          
          <p className={`mt-2 text-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {isVerified ? "Your account is now verified" : "Please enter the verification code sent to your email"}
          </p>
        </div>

        {isVerified ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`rounded-lg p-4 flex items-center ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-800'}`}
          >
            <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0" />
            <p>Email verified successfully! Redirecting to dashboard...</p>
          </motion.div>
        ) : (
          <>
            {message && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className={`rounded-lg p-4 flex items-center ${darkMode ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}
              >
                <EnvelopeIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                <p className="text-sm">{message}</p>
              </motion.div>
            )}
            
            {error && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className={`rounded-lg p-4 flex items-center ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}
              >
                <ExclamationCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </motion.div>
            )}
              
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Email
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      required
                      className={`block w-full pl-10 pr-3 py-3 border ${error && !email.trim() ? 'border-red-300' : darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${darkMode ? 'bg-gray-700 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-400'}`}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="code" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Verification Code
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      id="code"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={code}
                      onChange={handleCodeChange}
                      required
                      className={`block w-full pl-10 pr-3 py-3 border ${error && !code.trim() ? 'border-red-300' : darkMode ? 'border-gray-600' : 'border-gray-300'} tracking-widest text-center font-mono rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                      placeholder="123456"
                    />
                  </div>
                  <div className="text-xs text-right mt-1">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                      Enter the 6-digit code
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <motion.button
                  type="submit"
                  disabled={loading || !email.trim() || !code.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify Email'
                  )}
                </motion.button>
                
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading || resendDisabled || !email.trim()}
                    className={`inline-flex items-center text-sm ${
                      darkMode 
                        ? 'text-indigo-400 hover:text-indigo-300' 
                        : 'text-indigo-600 hover:text-indigo-800'
                    } disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150`}
                  >
                    {resendDisabled ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                        Resend in {countdown}s
                      </>
                    ) : (
                      'Resend verification code'
                    )}
                  </button>
                </div>
              </div>
            </form>
            
            <div className="mt-4 text-center">
              <Link 
                to="/login" 
                className={`text-sm ${
                  darkMode 
                    ? 'text-indigo-400 hover:text-indigo-300' 
                    : 'text-indigo-600 hover:text-indigo-800'
                } transition-colors duration-150`}
              >
                Back to login
              </Link>
            </div>
          </>
        )}
      </motion.div>
      
      <p className={`mt-8 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Having trouble? Contact our support team at support@powerquizzer.com
      </p>
    </div>
  );
};

export default VerifyEmail;